import { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { call, fmt, banner } from '../packs/packsApi';

const countsOf = (p) => {
  const ms = (p.workstreams || []).flatMap(w => w.milestones || []);
  return { workstreams: (p.workstreams || []).length, milestones: ms.length, tasks: ms.reduce((n, m) => n + (m.tasks || []).length, 0), blockers: (p.blockers || []).length };
};

// Build plan import: JSON file -> draft plan (replaces the project's plan).
// Once a pack freezes it as the baseline, imports are refused.
export default function PlanImport({ projectId }) {
  const navigate = useNavigate();
  const [versions, setVersions] = useState([]);
  const [file, setFile] = useState(null); // { name, json, counts }
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => onSnapshot(collection(db, 'clientProjects', projectId, 'planVersions'),
    s => setVersions(s.docs.map(d => ({ id: d.id, ...d.data() })))), [projectId]);
  const baseline = versions.find(v => v.status === 'baseline');
  const draft = versions.find(v => v.status === 'draft');

  const pick = async (f) => {
    setMsg(null); setFile(null);
    if (!f) return;
    try { const json = JSON.parse(await f.text()); setFile({ name: f.name, json, counts: countsOf(json) }); }
    catch { setMsg({ tone: 'danger', text: 'That file is not valid JSON.' }); }
  };
  const doImport = async () => {
    if (!window.confirm('Import as the draft plan? This replaces every workstream, milestone, task and blocker on this project.')) return;
    setBusy(true); setMsg(null);
    try {
      const r = await call('importPlan', { projectId, json: file.json });
      setMsg({ tone: 'success', text: `Imported: ${r.counts.tasks} tasks · ${r.counts.milestones} milestones · ${r.counts.workstreams} workstreams · ${r.counts.blockers} blockers.` });
      setFile(null);
    } catch (e) { setMsg({ tone: 'danger', text: e.message }); }
    setBusy(false);
  };

  return (
    <Card>
      <div style={{ fontSize: 14 }}>Build plan</div>
      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', margin: '2px 0 10px' }}>
        {baseline
          ? <>Baseline frozen {fmt(baseline.frozenAt)} by <span style={{ color: 'var(--color-brand)', cursor: 'pointer' }} onClick={() => navigate(`/packs/${baseline.packId}`)}>its signing pack</span>. Changes now go through change control. Plan SHA-256 <span style={{ fontFamily: 'var(--font-mono)' }}>{baseline.sha256.slice(0, 16)}…</span></>
          : draft ? `Draft imported ${fmt(draft.createdAt)}: ${draft.counts?.tasks} tasks · ${draft.counts?.milestones} milestones · ${draft.counts?.workstreams} workstreams. Link it to a signing pack to agree it.`
          : 'No plan imported. Upload the plan JSON to create a draft.'}
      </div>
      {msg && <div style={{ ...banner(msg.tone), marginBottom: 10 }}>{msg.text}</div>}
      {!baseline && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="file" accept=".json,application/json" style={{ fontSize: 12 }} onChange={e => pick(e.target.files[0])} />
          {file && <>
            <span style={{ fontSize: 12 }}>{file.counts.tasks} tasks · {file.counts.milestones} milestones · {file.counts.workstreams} workstreams · {file.counts.blockers} blockers</span>
            <Button size="sm" loading={busy} onClick={doImport}>Import as draft</Button>
          </>}
        </div>
      )}
    </Card>
  );
}
